define('CurrencySelector.EntryPoint',
  [
    'underscore',
    'GeoLocation.Service',
    'Profile.Model',
    'ProductViews.Price.View',
    'Transaction.Line.Views.Price.View',
    'Header.MiniCartItemCell.View',
    'Header.MiniCart.View',
    'Cart.Summary.View',
    'Cart.Item.Summary.View',
    'Item.Model'
  ],
  function (
    _,
    GeoLocationService,
    ProfileModel,
    ProductViewsPriceView,
    TransactionLineViewsPriceView,
    HeaderMiniCartItemCellView,
    HeaderMiniCartView,
    CartSummaryView,
    CartItemSummaryView,
    ItemModel
  ) {
    'use strict';

    return {
      mountToApp: function mountToApp(container) {

        var isActiveBowls = Boolean(SC && SC.ENVIRONMENT && SC.ENVIRONMENT.currentHostString && SC.ENVIRONMENT.currentHostString.indexOf('activebowls') !== -1);

        var environment = container.getComponent('Environment');
        var config = environment.getConfig('currencySelector');
        var henseliteAudId = config && config.henseliteAudPriceLevelId || '41';
        var henseliteNzdId = config && config.henseliteNzdPriceLevelId || '43';
        var activeBowlsId = config && config.activeBowlsPriceLevelId || '42';

        var b2cPriceLevel = { AUD: 'pricelevel' + henseliteAudId, NZD: 'pricelevel' + henseliteNzdId };
        var retailPriceLevelId = { AUD: henseliteAudId, NZD: henseliteNzdId };
        var activeBowlsPriceLevel = 'pricelevel' + activeBowlsId;
        var onlinePriceLevelId = '5';
        var sessionCurrencyKey = 'uc_currency';

        function getSessionCurrency() {
          try {
            return window.sessionStorage.getItem(sessionCurrencyKey);
          } catch (e) {
            return null;
          }
        }

        function setSessionCurrency(code) {
          try {
            window.sessionStorage.setItem(sessionCurrencyKey, code);
          } catch (e) {
            // ignore
          }
        }

        function getCurrentCurrencyCode() {
          var urlMatch = window.location.search.match(/[?&]cur=([A-Z]{3})/);
          if (urlMatch) {
            return urlMatch[1];
          }
          try {
            var current = SC && SC.ENVIRONMENT && SC.ENVIRONMENT.currentCurrency;
            return current && current.code || null;
          } catch (e) {
            return null;
          }
        }

        function withGst(formatted) {
          var label = ' (inc GST)';
          if (!formatted || formatted.indexOf(label) !== -1) {
            return formatted;
          }
          return formatted + label;
        }

        function withExGst(formatted) {
          var label = ' (exc GST)';
          if (!formatted || formatted.indexOf(label) !== -1) {
            return formatted;
          }
          return formatted + label;
        }

        function withCurrencyLabel(formatted, currencyCode) {
          if (!formatted || !currencyCode) {
            return formatted;
          }
          var label = currencyCode + ' ';
          return formatted.indexOf(label) === 0 ? formatted : label + formatted;
        }

        function isLoggedIn() {
          return ProfileModel.getInstance().get('isLoggedIn') === 'T';
        }

        function getCustomerPriceLevelId(currencyCode) {
          var priceLevelId = String(ProfileModel.getInstance().get('priceLevel') || '');
          if (!priceLevelId || priceLevelId === onlinePriceLevelId) {
            return retailPriceLevelId[currencyCode];
          }
          return priceLevelId;
        }

        // Prices stored in NetSuite pricelevel fields include AU GST (10%).
        // For NZD we strip the AU GST and apply NZ GST (15%) instead.
        function applyLocalGst(price, currencyCode) {
          var base = price / 1.10;
          var gstRate = currencyCode === 'NZD' ? 0.15 : 0.10;
          return base * (1 + gstRate);
        }

        function fmt(price) {
          return (Math.round(price * 100) / 100).toFixed(2);
        }

        _.extend(ItemModel.prototype, {
          getDefaultPrice: _.wrap(ItemModel.prototype.getDefaultPrice, function (fn, detailsObject) {
            if (!isActiveBowls && isLoggedIn()) {
              var currencyCode = getCurrentCurrencyCode();
              var priceLevelId = getCustomerPriceLevelId(currencyCode);
              var priceLevelKey = priceLevelId ? 'pricelevel' + priceLevelId : null;
              var levelPrice = priceLevelKey && this.get(priceLevelKey);
              var levelFormatted = priceLevelKey && this.get(priceLevelKey + '_formatted');
              if (levelPrice) {
                var displayPrice = applyLocalGst(levelPrice, currencyCode);
                var priceSymbol = (levelFormatted || '').replace(/[A-Z]{3}\s*/g, '').replace(/[\d,.\s]+.*$/, '');
                return { price: displayPrice, 'price_formatted': priceSymbol + fmt(displayPrice) };
              }
            }
            return fn.call(this, detailsObject);
          })
        });

        _.extend(ProductViewsPriceView.prototype, {
          getContext: _.wrap(ProductViewsPriceView.prototype.getContext, function (fn) {
            var context = fn.apply(this, _.toArray(arguments).slice(1));
            context.showComparePrice = false;
            var item = this.model.getItem && this.model.getItem() || this.model;
            var isTaxable = item.get('taxschedule') !== 'Non-Taxable';
            var currencyCode = getCurrentCurrencyCode();

            if (isActiveBowls) {
              var abPrice = item.get(activeBowlsPriceLevel);
              var abFormatted = item.get(activeBowlsPriceLevel + '_formatted');

              if (abPrice) {
                context.price = abPrice;
                context.priceFormatted = (isTaxable ? withGst(abFormatted) : abFormatted) || context.priceFormatted;
                context.rateFormatted = (isTaxable ? withGst(abFormatted) : abFormatted) || context.rateFormatted;
              }
              return context;
            }

            if (isLoggedIn()) {
              var priceLevelId = getCustomerPriceLevelId(currencyCode);
              var priceLevelKey = 'pricelevel' + priceLevelId;
              var levelPrice = item.get(priceLevelKey);
              var levelFormatted = item.get(priceLevelKey + '_formatted');

              if (levelPrice) {
                var displayPrice = applyLocalGst(levelPrice, currencyCode);
                var levelSymbol = (levelFormatted || '').replace(/[A-Z]{3}\s*/g, '').replace(/[\d,.\s]+.*$/, '');
                var formattedPrice = levelSymbol + fmt(displayPrice);
                context.price = displayPrice;
                context.priceFormatted = withCurrencyLabel(isTaxable ? withGst(formattedPrice) : formattedPrice, currencyCode);
                context.rateFormatted = withCurrencyLabel(isTaxable ? withGst(formattedPrice) : formattedPrice, currencyCode);
                return context;
              }

              if (context.priceFormatted) {
                context.priceFormatted = withCurrencyLabel(isTaxable ? withGst(context.priceFormatted) : context.priceFormatted, currencyCode);
              }
              if (context.rateFormatted) {
                context.rateFormatted = withCurrencyLabel(isTaxable ? withGst(context.rateFormatted) : context.rateFormatted, currencyCode);
              }
              return context;
            }

            var guestPriceLevelKey = b2cPriceLevel[currencyCode];
            if (guestPriceLevelKey) {
              var guestPrice = item.get(guestPriceLevelKey);
              var guestFormatted = item.get(guestPriceLevelKey + '_formatted');
              if (guestPrice) {
                var adjustedPrice = applyLocalGst(guestPrice, currencyCode);
                var priceSymbol = (guestFormatted || '').replace(/[\d,.\s]+.*$/, '');
                var adjustedFormatted = priceSymbol + fmt(adjustedPrice);
                context.price = adjustedPrice;
                context.priceFormatted = withCurrencyLabel(isTaxable ? withGst(adjustedFormatted) : adjustedFormatted, currencyCode) || context.priceFormatted;
                context.rateFormatted = withCurrencyLabel(isTaxable ? withGst(adjustedFormatted) : adjustedFormatted, currencyCode) || context.rateFormatted;
              }
            }

            return context;
          })
        });

        _.extend(TransactionLineViewsPriceView.prototype, {
          getContext: _.wrap(TransactionLineViewsPriceView.prototype.getContext, function (fn) {
            var context = fn.apply(this, _.toArray(arguments).slice(1));
            context.showComparePrice = false;
            var item = this.model.get('item') || this.model;
            var isTaxable = item.get('taxschedule') !== 'Non-Taxable';
            var currencyCode = getCurrentCurrencyCode();

            if (isActiveBowls) {
              var abPrice = item.get(activeBowlsPriceLevel);
              var abFormatted = item.get(activeBowlsPriceLevel + '_formatted');
              if (abPrice) {
                context.price = abPrice;
                context.priceFormatted = (isTaxable ? withGst(abFormatted) : abFormatted) || context.priceFormatted;
                context.rateFormatted = (isTaxable ? withGst(abFormatted) : abFormatted) || context.rateFormatted;
              }
              return context;
            }

            if (isLoggedIn()) {
              var priceLevelId = getCustomerPriceLevelId(currencyCode);
              var priceLevelKey = 'pricelevel' + priceLevelId;
              var levelPrice = item.get(priceLevelKey);
              var levelFormatted = item.get(priceLevelKey + '_formatted');

              if (levelPrice) {
                var displayPrice = applyLocalGst(levelPrice, currencyCode);
                var levelSymbol = (levelFormatted || '').replace(/[A-Z]{3}\s*/g, '').replace(/[\d,.\s]+.*$/, '');
                var formattedPrice = levelSymbol + fmt(displayPrice);
                context.price = displayPrice;
                context.priceFormatted = withCurrencyLabel(isTaxable ? withGst(formattedPrice) : formattedPrice, currencyCode);
                context.rateFormatted = withCurrencyLabel(isTaxable ? withGst(formattedPrice) : formattedPrice, currencyCode);
                return context;
              }

              if (context.priceFormatted) {
                context.priceFormatted = withCurrencyLabel(isTaxable ? withGst(context.priceFormatted) : context.priceFormatted, currencyCode);
              }
              if (context.rateFormatted) {
                context.rateFormatted = withCurrencyLabel(isTaxable ? withGst(context.rateFormatted) : context.rateFormatted, currencyCode);
              }
              return context;
            }

            var guestPriceLevelKey = b2cPriceLevel[currencyCode];
            if (guestPriceLevelKey) {
              var guestPrice = item.get(guestPriceLevelKey);
              var guestFormatted = item.get(guestPriceLevelKey + '_formatted');
              if (guestPrice) {
                var adjustedPrice = applyLocalGst(guestPrice, currencyCode);
                var priceSymbol = (guestFormatted || '').replace(/[\d,.\s]+.*$/, '');
                var adjustedFormatted = priceSymbol + fmt(adjustedPrice);
                context.price = adjustedPrice;
                context.priceFormatted = withCurrencyLabel(isTaxable ? withGst(adjustedFormatted) : adjustedFormatted, currencyCode) || context.priceFormatted;
                context.rateFormatted = withCurrencyLabel(isTaxable ? withGst(adjustedFormatted) : adjustedFormatted, currencyCode) || context.rateFormatted;
              }
            }

            return context;
          })
        });

        _.extend(HeaderMiniCartItemCellView.prototype, {
          getContext: _.wrap(HeaderMiniCartItemCellView.prototype.getContext, function (fn) {
            var context = fn.apply(this, _.toArray(arguments).slice(1));
            context.showComparePrice = false;
            var item = this.model.get('item') || this.model;
            var isTaxable = item.get('taxschedule') !== 'Non-Taxable';
            var currencyCode = getCurrentCurrencyCode();

            if (isActiveBowls) {
              var abPrice = item.get(activeBowlsPriceLevel);
              var abFormatted = item.get(activeBowlsPriceLevel + '_formatted');
              if (abPrice) {
                context.price = abPrice;
                context.priceFormatted = (isTaxable ? withGst(abFormatted) : abFormatted) || context.priceFormatted;
                context.rateFormatted = (isTaxable ? withGst(abFormatted) : abFormatted) || context.rateFormatted;
              }
              return context;
            }

            if (isLoggedIn()) {
              var priceLevelId = getCustomerPriceLevelId(currencyCode);
              var priceLevelKey = 'pricelevel' + priceLevelId;
              var levelPrice = item.get(priceLevelKey);
              var levelFormatted = item.get(priceLevelKey + '_formatted');

              if (levelPrice) {
                var displayPrice = applyLocalGst(levelPrice, currencyCode);
                var levelSymbol = (levelFormatted || '').replace(/[A-Z]{3}\s*/g, '').replace(/[\d,.\s]+.*$/, '');
                var formattedPrice = levelSymbol + fmt(displayPrice);
                context.price = displayPrice;
                context.priceFormatted = withCurrencyLabel(isTaxable ? withGst(formattedPrice) : formattedPrice, currencyCode);
                context.rateFormatted = withCurrencyLabel(isTaxable ? withGst(formattedPrice) : formattedPrice, currencyCode);
                return context;
              }

              if (context.priceFormatted) {
                context.priceFormatted = withCurrencyLabel(isTaxable ? withGst(context.priceFormatted) : context.priceFormatted, currencyCode);
              }
              if (context.rateFormatted) {
                context.rateFormatted = withCurrencyLabel(isTaxable ? withGst(context.rateFormatted) : context.rateFormatted, currencyCode);
              }
              return context;
            }

            var guestPriceLevelKey = b2cPriceLevel[currencyCode];
            if (guestPriceLevelKey) {
              var guestPrice = item.get(guestPriceLevelKey);
              var guestFormatted = item.get(guestPriceLevelKey + '_formatted');
              if (guestPrice) {
                var adjustedPrice = applyLocalGst(guestPrice, currencyCode);
                var priceSymbol = (guestFormatted || '').replace(/[\d,.\s]+.*$/, '');
                var adjustedFormatted = priceSymbol + fmt(adjustedPrice);
                context.price = adjustedPrice;
                context.priceFormatted = withCurrencyLabel(isTaxable ? withGst(adjustedFormatted) : adjustedFormatted, currencyCode) || context.priceFormatted;
                context.rateFormatted = withCurrencyLabel(isTaxable ? withGst(adjustedFormatted) : adjustedFormatted, currencyCode) || context.rateFormatted;
              }
            }

            return context;
          })
        });

        _.extend(HeaderMiniCartView.prototype, {
          getContext: _.wrap(HeaderMiniCartView.prototype.getContext, function (fn) {
            var context = fn.apply(this, _.toArray(arguments).slice(1));
            context.showComparePrice = false;

            if (context.isLoading) {
              return context;
            }

            var currencyCode = getCurrentCurrencyCode();
            var priceLevelKey;

            if (isActiveBowls) {
              priceLevelKey = activeBowlsPriceLevel;
            } else if (isLoggedIn()) {
              if (context.subtotalFormatted) {
                context.subtotalFormatted = withCurrencyLabel(withExGst(context.subtotalFormatted), currencyCode);
              }
              return context;
            } else {
              priceLevelKey = b2cPriceLevel[currencyCode];
            }

            if (!priceLevelKey) {
              return context;
            }

            var lines = this.model.get('lines');
            if (!lines || !lines.length) {
              return context;
            }

            var subtotal = 0;
            var symbol = '';

            lines.each(function (line) {
              var item = line.get('item');
              if (!item) {
                return;
              }
              subtotal += (parseFloat(item.get(priceLevelKey)) || 0) * (parseFloat(line.get('quantity')) || 1);
              if (!symbol) {
                symbol = (item.get(priceLevelKey + '_formatted') || '').replace(/[\d,.\s]+.*$/, '');
              }
            });

            if (subtotal) {
              var subtotalEx = Math.round(subtotal / 1.10 * 100) / 100;
              context.subtotalFormatted = withCurrencyLabel(withExGst(symbol + fmt(subtotalEx)), currencyCode);
            }

            return context;
          })
        });

        _.extend(CartItemSummaryView.prototype, {
          getContext: _.wrap(CartItemSummaryView.prototype.getContext, function (fn) {
            var context = fn.apply(this, _.toArray(arguments).slice(1));
            context.showComparePrice = false;
            var currencyCode = getCurrentCurrencyCode();
            var priceLevelKey;

            if (isActiveBowls) {
              priceLevelKey = activeBowlsPriceLevel;
            } else if (isLoggedIn()) {
              var lineItem = this.model.get('item') || this.model;
              var isTaxable = lineItem.get('taxschedule') !== 'Non-Taxable';
              if (context.totalFormatted) {
                if (isTaxable) {
                  var rawTotal = parseFloat((context.totalFormatted || '').replace(/[^0-9.]/g, '')) || 0;
                  var totalSymbol = (context.totalFormatted || '').replace(/[\d,.\s]+.*$/, '');
                  var gstRate = currencyCode === 'NZD' ? 0.15 : 0.10;
                  var incTotal = rawTotal * (1 + gstRate);
                  context.totalFormatted = withCurrencyLabel(withGst(totalSymbol + fmt(incTotal)), currencyCode);
                } else {
                  context.totalFormatted = withCurrencyLabel(context.totalFormatted, currencyCode);
                }
              }
              return context;
            } else {
              priceLevelKey = b2cPriceLevel[currencyCode];
            }

            if (!priceLevelKey) {
              return context;
            }

            var item = this.model.get('item') || this.model;
            var itemPrice = parseFloat(item.get(priceLevelKey)) || 0;
            var itemFormatted = item.get(priceLevelKey + '_formatted') || '';

            if (itemPrice) {
              var qty = parseFloat(this.model.get('quantity')) || 1;
              var symbol = itemFormatted.replace(/[\d,.\s]+.*$/, '');
              var adjustedItemPrice = applyLocalGst(itemPrice, currencyCode);
              context.totalFormatted = withCurrencyLabel(withGst(symbol + fmt(adjustedItemPrice * qty)), currencyCode);
            }

            return context;
          })
        });

        _.extend(CartSummaryView.prototype, {
          getContext: _.wrap(CartSummaryView.prototype.getContext, function (fn) {
            var context = fn.apply(this, _.toArray(arguments).slice(1));
            context.showComparePrice = false;
            var estimateCountry = isActiveBowls ? 'AU' : getCurrentCurrencyCode() === 'NZD' ? 'NZ' : 'AU';
            if (context.countries) {
              context.countries = _.filter(context.countries, function (c) {
                return c.code === estimateCountry;
              });
              context.defaultCountry = estimateCountry;
              context.singleCountry = true;
              context.singleCountryCode = estimateCountry;
              context.singleCountryName = context.countries.length ? context.countries[0].name : estimateCountry;
            }

            var summary = context.summary;
            if (!summary) {
              return context;
            }

            var currencyCode = getCurrentCurrencyCode();

            if (isActiveBowls) {
              var abLines = context.model && context.model.get('lines');
              if (!abLines || !abLines.length) {
                return context;
              }
              var abSubtotal = 0;
              var abSymbol = '';
              abLines.each(function (line) {
                var item = line.get('item');
                if (!item) {
                  return;
                }
                abSubtotal += (parseFloat(item.get(activeBowlsPriceLevel)) || 0) * (parseFloat(line.get('quantity')) || 1);
                if (!abSymbol) {
                  abSymbol = (item.get(activeBowlsPriceLevel + '_formatted') || '').replace(/[\d,.]+.*$/, '');
                }
              });
              if (!abSubtotal) {
                return context;
              }
              var abSubtotalEx = Math.round(abSubtotal / 1.10 * 100) / 100;
              var abShipping = summary.shippingcost || 0;
              var abGst = Math.round((abSubtotalEx + abShipping) * 0.10 * 100) / 100;
              var abTotal = abSubtotalEx + abShipping + abGst;
              summary.subtotal = abSubtotalEx;
              summary['subtotal_formatted'] = withExGst(abSymbol + fmt(abSubtotalEx));
              summary.taxtotal = abGst;
              summary['taxtotal_formatted'] = abSymbol + fmt(abGst);
              summary.total = abTotal;
              summary['total_formatted'] = withGst(abSymbol + fmt(abTotal));
              return context;
            }

            if (isLoggedIn()) {
              if (summary['subtotal_formatted']) {
                summary['subtotal_formatted'] = withCurrencyLabel(withExGst(summary['subtotal_formatted']), currencyCode);
              }
              if (summary['taxtotal_formatted']) {
                summary['taxtotal_formatted'] = withCurrencyLabel(summary['taxtotal_formatted'], currencyCode);
              }
              if (summary['shippingcost_formatted']) {
                summary['shippingcost_formatted'] = withCurrencyLabel(summary['shippingcost_formatted'], currencyCode);
              }
              if (summary['total_formatted']) {
                summary['total_formatted'] = withCurrencyLabel(withGst(summary['total_formatted']), currencyCode);
              }
              return context;
            }

            var lines = context.model && context.model.get('lines');
            if (!lines || !lines.length) {
              return context;
            }

            var priceLevelKey = b2cPriceLevel[currencyCode];
            if (!priceLevelKey) {
              return context;
            }

            var subtotal = 0;
            var symbol = '';

            lines.each(function (line) {
              var item = line.get('item');
              if (!item) {
                return;
              }
              subtotal += (parseFloat(item.get(priceLevelKey)) || 0) * (parseFloat(line.get('quantity')) || 1);
              if (!symbol) {
                symbol = (item.get(priceLevelKey + '_formatted') || '').replace(/[\d,.]+.*$/, '');
              }
            });

            if (!subtotal) {
              return context;
            }

            var gstRate = currencyCode === 'NZD' ? 0.15 : 0.10;
            var subtotalEx = Math.round(subtotal / 1.10 * 100) / 100;
            var shipping = summary.shippingcost || 0;
            var gst = Math.round((subtotalEx + shipping) * gstRate * 100) / 100;
            var total = subtotalEx + shipping + gst;

            summary.subtotal = subtotalEx;
            summary['subtotal_formatted'] = withCurrencyLabel(withExGst(symbol + fmt(subtotalEx)), currencyCode);
            summary.taxtotal = gst;
            summary['taxtotal_formatted'] = withCurrencyLabel(symbol + fmt(gst), currencyCode);
            summary.total = total;
            summary['total_formatted'] = withCurrencyLabel(withGst(symbol + fmt(total)), currencyCode);

            if (summary['shippingcost_formatted']) {
              summary['shippingcost_formatted'] = withCurrencyLabel(summary['shippingcost_formatted'], currencyCode);
            }

            return context;
          })
        });


        if (isActiveBowls || isLoggedIn()) {
          return;
        }

        GeoLocationService.getLocation().done(function (location) {
          var targetCurrency = location.isNZ ? 'NZD' : 'AUD';
          var currentCurrency = getCurrentCurrencyCode();

          if (getSessionCurrency() === targetCurrency && currentCurrency === targetCurrency) {
            return;
          }

          if (currentCurrency && currentCurrency === targetCurrency) {
            if (window.location.search.indexOf('cur=') !== -1) {
              history.replaceState(null, '', window.location.pathname + window.location.hash);
            }
            setSessionCurrency(targetCurrency);
            return;
          }

          window.location.replace(window.location.pathname + '?cur=' + targetCurrency);
        });
      }
    };
  }
);
